// @ts-strict-ignore
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleConstants } from '../constants/article-constants';
import { lastValueFrom, Observable, of, Subject, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { Parameters, WagoApiClient } from '@shared/service/wago-api.client';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { NumberUtils } from '@shared/util/number-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleTechnicalInfoVO, ArticleTypeClassVO, ArticleTypeVO, ArticleVO } from '../model/vo/article.vo';
import { ConnectionAreaSearchQueryVO } from '@project/components/article/model/vo/connection-area-search-query.vo';
import { ConnectionAreaSearchQueryDTO } from '../model/vo/connection-area-search-query.dto';
import { DrillingHoleDataVO, DrillingholePresetVO } from '@project/actions/drilling-hole/drilling-hole.vo';
import { ArticleSearchResultVO } from '../model/vo/article-search-result.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { FavoriteArticlesWrapperVO, FavoriteArticleVO } from '@user/components/favorites/models/favorite-article.vo';
import { ProjectVO } from '@project/model/vo/project.vo';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ItemListObjectVO } from './model/item-list-object.vo';
import { PriceCalculationRequestProjectVO, PriceCalculationRequestVO, PriceCalculationResultVO } from '@project/model/vo/price-calculation-data.vo';
import { ArticleTechDataValueVO } from '../model/vo/article-tech-data.vo';
import { HttpParams } from '@angular/common/http';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { ArticleColorVariantsInfoVO } from '../model/vo/article-color-variants-info.vo';
import { ArticleNosToHeadMappingVO } from '../model/vo/articlesnos-to-head-mapping';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';
import { ConnectionSearchResultVO } from '@project/components/article/model/vo/connection-search-result.vo';
import { AccessorySearchResultVO } from '@project/components/article/model/vo/accessory-search-result.vo';
import { ArticleAccessoryInfoVO } from '../model/vo/article-accessory-info-vo';
import { OldArticleNumberMappingVO } from '../model/vo/old-article-number-mapping.vo';
import { TechDataSearchDTO } from '../model/vo/tech-data-search.dto';
import { ArticleWithTechnicalPropertiesDTO, TechnicalRequirementsVO } from '../properties/technical/technical-requirements';
import { LabelingCategory } from '../labeling/labeling.category';

export interface GetLatestArticlesNotification {
  success: boolean,
  articles: ArticleVO[]
}

type SearchArticlesByTechnicalPropertiesRequestDTO = {
  locale: string,
  searchText: string,
  articleTypeIds: number[],
  series: string[],
  technicalRequirements: TechnicalRequirementsVO[],
  technicalProperties: string[],
}

class ArticlesWithTechnicalPropertiesRequestDTO {

  constructor(
    public articleIds: number[],
    public propertyNames: string[],
  ) {}
}

class WagoArticleConnectionQueryDTO {

  constructor(
    public connectionAreaSearchQuery: ConnectionAreaSearchQueryVO,
    public count: number,
    public position: number,
    public useSaddleJumper: boolean,
    public crossSection: number | undefined,
    public color: string | undefined) {
  }
}

class WagoLabelingArticleConnectionQueryDTO extends WagoArticleConnectionQueryDTO {

  constructor(
    connectionAreaSearchQuery: ConnectionAreaSearchQueryVO,
    count: number,
    position: number,
    useSaddleJumper: boolean,
    crossSection: number | undefined,
    color: string | undefined,
    public labelingCategories: LabelingCategory[]) {
    super(connectionAreaSearchQuery, count, position, useSaddleJumper, crossSection, color);
  }
}

export interface MountingRailInfo {
  articleId: number;
  articleNo: string;
  displayName: string;
  color: string;
  mountingRailType: string;
}

@Injectable({ providedIn: 'root' })
export class ArticleDataService {
  private readonly searchByArticleNoUrl: string = 'article/search/by-number';
  private readonly loadArticlesByArticleNumberUrl: string = 'article/search/by-number-and-version';
  private readonly loadArticleByArticleNumberUrl: string = 'article/by-number-and-version';
  private readonly loadArticleByArticleTypeUrl: string = 'article/type/';
  private readonly loadArticleByArticleIdUrl: string = 'article/';
  private readonly getArticlesByRequirementsUrl: string = 'article/search';
  private readonly getBridgeArticlesByRequirementsUrl: string = 'article/search/favorite/bridge';
  private readonly searchAccessoriesUrl: string = 'article/search/accessories';
  private readonly searchArticlesByTypeUrl: string = 'article/search/type/';
  private readonly getArticlesByHeadsUrl: string = 'article/search/by-heads';
  private readonly getArticleHeadsByArticleNosUrl: string = 'article/search/heads/by-articlenos';
  private readonly getLatestArticlesByNoUrl: string = 'article/search/latest/by-numbers';
  private readonly getLatestArticlesByIdsUrl: string = 'article/search/latest/by-ids';
  private readonly getNonExistingByNumbersUrl: string = 'article/non-existing/by-numbers';
  private readonly getTechDataUrl: string = 'article/techdata/search';
  private readonly getFavoriteArticlesUrl: string = 'article/favorite';
  private readonly loadAllArticleTypeClassesURL: string = 'article/type-classes';
  private readonly loadAllMCSArticleTypeClassesURL: string = 'article/type-class/mcs';
  private readonly calculatePriceUrl: string = 'article/project-price';
  private readonly loadAllDrillingHolesUrl: string = 'article/drilling-hole/all';
  private readonly getItemListByProjectUrl: string = 'article/item-list/by-project';
  private readonly getItemListByProjectIdUrl: string = 'article/item-list/by-project-id';
  private readonly getArticleColorVariantsUrl: string = 'article/variants/colors';
  private readonly findTypeIdsByArticleIdsUrl: string = 'article/type-id';
  private readonly loadArticleByOldArticleNumbersUrl: string = 'article/old-article-numbers';

  private getLatestArticlesSubject: Subject<GetLatestArticlesNotification> = new Subject<GetLatestArticlesNotification>();

  constructor(
    private modelConversionService: ModelConversionService,
    private wagoApiClient: WagoApiClient,
    private localeResourceManager: LocaleResourceManager,
  ) {}

  public get getLatestArticles$(): Observable<GetLatestArticlesNotification> {
    return this.getLatestArticlesSubject.asObservable();
  }

  public notifyGetLatestArticles(success: boolean, articles: ArticleVO[] = null): void {
    const notification: GetLatestArticlesNotification = {
      success: success,
      articles: articles
    };
    this.getLatestArticlesSubject.next(notification);
  }

  public searchByArticleNo(articleNumber: string, articleTypeIds: number[] = null): Observable<ArticleWithTechnicalPropertiesDTO[]> {
    if (StringUtils.isEmpty(articleNumber)) {
      return of([]);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('articleNo', articleNumber);
    httpParams = httpParams.append('articleTypeIds', articleTypeIds ? articleTypeIds.toString() : null);
    httpParams = httpParams.append('locale', this.localeResourceManager.currentLanguage.langKey);
    const requestOptions = {
      params: httpParams,
    };
    const parameters: Parameters = { overlayType: null };
    return this.wagoApiClient.get<ArticleWithTechnicalPropertiesDTO[]>(this.searchByArticleNoUrl, requestOptions, parameters);
  }

  public loadArticleByIdOrArticleNumberAndVersion(name: string, version?: number): Observable<ArticleVO> {
    if (ArticleUtils.isStringArticleId(name)) {
        return this.loadArticleByArticleId(+name, version);
    } else {
        return this.loadArticleByArticleNumber(name, version);
    }
  }

  public loadArticleByArticleId(articleId: number, articleVersion?: number): Observable<ArticleVO> {
    if (!NumberUtils.isNumber(articleId) || !Number.isInteger(articleId)) {
      return of(null);
    }
    let version: number = articleVersion;
    if (!Number.isInteger(version)) {
      version = -1;
    }
    const requestOptions = {
      params: new HttpParams().append('version', version.toString()),
    };
    return this.wagoApiClient
      .get(this.loadArticleByArticleIdUrl + articleId, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertArticleVO(response)));
  }

  public loadArticleByArticleNumber(articleNumber: string, articleVersion?: number): Observable<ArticleVO> {
    if (StringUtils.isEmpty(articleNumber)) {
      return of(null);
    }

    const requestOptions = this.getArticleNoAndVersionRequestOptions(articleVersion, articleNumber);

    return this.wagoApiClient
      .get(this.loadArticleByArticleNumberUrl, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertArticleVO(response)));
  }

  public loadArticlesByArticleNumber(articleNumber: string, articleVersion?: number): Observable<ArticleVO[]> {
    if (StringUtils.isEmpty(articleNumber)) {
      return of(null);
    }
    const requestOptions = this.getArticleNoAndVersionRequestOptions(articleVersion, articleNumber);

    return this.wagoApiClient
      .get(this.loadArticlesByArticleNumberUrl, requestOptions)
      .pipe(map((response: any[]) => this.modelConversionService.convertArticleVOs(response)));
  }

  private getArticleNoAndVersionRequestOptions(articleVersion: number, articleNumber: string) {
    let version: number = articleVersion;
    if (!Number.isInteger(version)) {
      version = -1;
    }

    let params: HttpParams = new HttpParams();
    params = params.append('articleNo', articleNumber);
    params = params.append('version', version.toString());
    const requestOptions = {
      params: params,
    };
    return requestOptions;
  }

  public loadArticleByArticleType(typeId: number): Observable<ArticleVO> {
    if (!NumberUtils.isNumber(typeId)) {
      return of(null);
    }

    return this.wagoApiClient
      .get(this.loadArticleByArticleTypeUrl + typeId)
      .pipe(map((response) => this.modelConversionService.convertArticleVO(response)));
  }

  public loadRepresentationArticle(articleId: number, representationName: string): Observable<ArticleVO> {
    if (!NumberUtils.isNumber(articleId) || StringUtils.isEmpty(representationName)) {
      return of(null);
    }
    const requestOptions = {
      params: new HttpParams().append('representationName', representationName),
    };
    return this.wagoApiClient
      .get(`article/${articleId}/search/by-representation-name`, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertArticleVO(response)));
  }

  public loadTechnicalInfoByArticleIds(articleIds: number[], isOverlayRequired: boolean): Observable<Map<number, ArticleTechnicalInfoVO>> {
    if (ArrayUtils.isEmpty(articleIds)) {
      return of(new Map<number, ArticleTechnicalInfoVO>());
    }

    const requestOptions = {
      params: new HttpParams().append('articleIds', articleIds.join()),
    };

    const parameters: Parameters = isOverlayRequired ? {} : { overlayType: null };

    return this.wagoApiClient
      .get(`article/technical-info/by-ids`, requestOptions, parameters)
      .pipe(map((response) => this.modelConversionService.convertArticleTechnicalInfoMap(response)));
  }

  public searchAccessories(
    connectionAreaSearchQueryVO: ConnectionAreaSearchQueryVO,
    excludedArticleTypes: string[],
    isOverlayRequired: boolean = true
  ): Observable<AccessorySearchResultVO> {
    if (!connectionAreaSearchQueryVO) {
      return throwError('no data to search for');
    }

    const dto: ConnectionAreaSearchQueryDTO = {
      connectionAreaSearchQueryVO,
      posFrom: undefined,
      count: undefined,
      excludedArticleTypes,
      locale: this.localeResourceManager.currentLanguage.langKey
    };

    const parameters: Parameters = {
      isErrorHandlingRequired: false,
      overlayType: isOverlayRequired ? OverlayBackground.color : null,
    };

    const url: string = this.searchAccessoriesUrl;
    return this.wagoApiClient.post(url, dto, null, parameters).pipe(map((data: any) => this.modelConversionService.convertAccessorySearchResultVO(data)));
  }

  public getBridgeFavoriteArticlesByRequirements(connectionAreaSearchQuery: ConnectionAreaSearchQueryVO,
                                                 useSaddleJumper: boolean,
                                                 crossSection: number | undefined,
                                                 color: string | undefined): Observable<ConnectionSearchResultVO> {
    const dto: WagoArticleConnectionQueryDTO =
      new WagoArticleConnectionQueryDTO(
        connectionAreaSearchQuery,
        5,
        0,
        useSaddleJumper,
        crossSection,
        color);

    return this.wagoApiClient
      .post(this.getBridgeArticlesByRequirementsUrl, dto, null)
      .pipe(map((data: any) => this.modelConversionService.convertConnectionSearchResultVO(data)));
  }

  public getArticlesByRequirements(
    connectionAreaSearchQueryVO: ConnectionAreaSearchQueryVO,
    offset: number,
    maxResults: number,
    excludedArticleTypes: string[],
    isOverlayRequired: boolean = true): Observable<ConnectionSearchResultVO> {

    const dto: ConnectionAreaSearchQueryDTO = {
      connectionAreaSearchQueryVO,
      posFrom: offset,
      count: maxResults,
      excludedArticleTypes,
      locale: this.localeResourceManager.currentLanguage.langKey
    };
    const parameters: Parameters = {
      isErrorHandlingRequired: false,
      overlayType: isOverlayRequired ? OverlayBackground.color : null,
    };
    return this.wagoApiClient
      .post(this.getArticlesByRequirementsUrl, dto, null, parameters)
      .pipe(map((data: any) => this.modelConversionService.convertConnectionSearchResultVO(data)));
  }

  public loadMountingRailInfos(): Observable<MountingRailInfo[]> {
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('locale', this.localeResourceManager.currentLanguage.langKey);
    const requestOptions = {
      params: httpParams,
    };

    return this.wagoApiClient
      .get<MountingRailInfo[]>(`article/search/mounting-rail-infos`, requestOptions);
  }

  public searchMatchingRails(article: ArticleVO): Observable<ArticleVO[]> {

    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('locale', this.localeResourceManager.currentLanguage.langKey);
    const requestOptions = {
      params: httpParams,
    };

    return this.wagoApiClient.post<ArticleVO[]>(`article/search/matching-mounting-rails`, article, requestOptions);
  }

  public async loadDrillingHolePresetAfterProjectLoadedHandler(data: {carrierArticleId: number, mode: string}): Promise<DrillingholePresetVO> {
    let preset: DrillingholePresetVO;
    try {
      preset = await this.loadDrillingholePresetByCarrierArticleId(data.carrierArticleId).toPromise();
    } catch (error) {
      Log.error('Error: failed to load drilling holes: ', error);
    }
    return preset;
  }

  public findArticlesByType(typeId: number, posFrom: number, count: number): Observable<ArticleSearchResultVO> {
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('posFrom', posFrom.toString());
    httpParams = httpParams.append('count', count.toString());
    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(this.searchArticlesByTypeUrl + typeId, requestOptions, { isErrorHandlingRequired: false })
      .pipe(map((response) => this.modelConversionService.convertArticleSearchResultVO(response)));
  }

  public loadDrillingholePresetByCarrierArticleId(carrierArticleId: number): Observable<DrillingholePresetVO> {
    if (NumberUtils.isNotNumber(carrierArticleId) || carrierArticleId === ArticleConstants.DEFAULT_FREE_MOUNTING_ARTICLE_ID) {
      return of(null);
    }

    return this.wagoApiClient.get(`article/${carrierArticleId}/info/drilling-hole`).pipe(
      map((response: any) => {
        return this.modelConversionService.convertDrillingholePresetVO(response);
      })
    );
  }

  public getArticlesByHeads(articles: ArticleVO[]): Observable<ArticleVO[]> {
    if (ArrayUtils.isEmpty(articles)) {
      return of(null);
    }
    return this.wagoApiClient
      .post(this.getArticlesByHeadsUrl, articles)
      .pipe(map((data: any) => this.modelConversionService.convertArticleVOs(data)));
  }

  public getArticleTechData(techData: string[], articleTypeIds: number[], locale: string): Observable<ArticleTechDataValueVO[]> {
    const articleTechData: TechDataSearchDTO = {
      techData,
      locale,
      articleTypeIds,
    };
    return this.wagoApiClient
      .post(this.getTechDataUrl, articleTechData)
      .pipe(map((data: any) => this.modelConversionService.convertArticleTechDataValueVOs(data)));
  }

  public getLatestArticlesByNoObservable(articleNumbers: string[]): Observable<ArticleVO[]> {
    if (ArrayUtils.isEmpty(articleNumbers)) {
      return of([]);
    }
    const requestOptions = {
      params: new HttpParams().append('articleNos', articleNumbers.join()),
    };
    return this.wagoApiClient
      .get(this.getLatestArticlesByNoUrl, requestOptions, {isErrorHandlingRequired: false})
      .pipe(
        map((data: any) => this.modelConversionService.convertArticleVOs(data))
      );
  }

  public getNonExistingArticlesByNumbersAsObservable(articleNumbers: string[]): Observable<string[]> {
    if (ArrayUtils.isEmpty(articleNumbers)) {
      return of([]);
    }
    const requestOptions = {
      params: new HttpParams().append('articleNumbers', articleNumbers.join()),
    };
    return this.wagoApiClient
      .get(this.getNonExistingByNumbersUrl, requestOptions, {
        askToElevateUserFromGuestByLoggingIn: true,
        isErrorHandlingRequired: false,
      })
      .pipe(
        map((data: string[]) => data)
      );
  }

  public getLabelingFavoriteArticlesByRequirementsAndLabelingCategories(
    connectionAreaSearchQueryVO: ConnectionAreaSearchQueryVO,
    labelingCategories: LabelingCategory[],
  ): Observable<ConnectionSearchResultVO> {
    const dto: WagoLabelingArticleConnectionQueryDTO = new WagoLabelingArticleConnectionQueryDTO(
      connectionAreaSearchQueryVO,
      1,
      0,
      false,
      undefined,
      undefined,
      labelingCategories);

    const request: Observable<Response> = this.wagoApiClient.post(
      'article/favorite/labeling/count',
      dto,
      undefined,
      { isErrorHandlingRequired: false }
    );
    return request.pipe(
      map((object: any) => this.modelConversionService.convertConnectionSearchResultVO(object))
    );
  }

  public getLatestArticles(articleIds: number[]): Observable<ArticleVO[]> {
    if (ArrayUtils.isEmpty(articleIds)) {
      return of(null);
    }
    return this.wagoApiClient.get(this.getLatestArticlesByIdsUrl + '?articleIds=' + articleIds)
      .pipe(map((data: any) => this.modelConversionService.convertArticleVOs(data)));
  }

  public async getAllArticlesByType(typeId: number): Promise<ArticleVO[]> {
    return await lastValueFrom(this.getAllArticlesByTypeAsObservable(typeId));
  }

  public getAllArticlesByTypeAsObservable(typeId: number): Observable<ArticleVO[]> {
    if (NumberUtils.isNotNumber(typeId)) {
      return of([]);
    }
    return this.wagoApiClient
      .get(`${this.getArticlesByRequirementsUrl}/all/type/${typeId}`)
      .pipe(map((response: any[]) => this.modelConversionService.convertArticleVOs(response)));
  }

  public getFavoriteArticles(articleTypeIds: number[], isOverlayRequired: boolean = true): Observable<FavoriteArticlesWrapperVO[]> {
    if (ArrayUtils.isEmpty(articleTypeIds)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('articleTypeIds', articleTypeIds.join());
    httpParams = httpParams.append('language', this.localeResourceManager.currentLanguage.langKey);
    const requestOptions = {
      params: httpParams,
    };
    const parameters: Parameters = {
      isErrorHandlingRequired: false,
      overlayType: isOverlayRequired ? OverlayBackground.color : null,
    };
    return this.wagoApiClient
      .get(this.getFavoriteArticlesUrl, requestOptions, parameters)
      .pipe(map((data: any) => this.modelConversionService.convertFavoriteArticlesWrapperVOs(data)));
  }

  public updateFavoriteArticles(articlesToUpdateOrder: FavoriteArticleVO[]): Observable<any> {
    const wagoArticleFavoriteDTO: any = {
      articlesToUpdateOrder,
    };
    return this.wagoApiClient
      .post(this.getFavoriteArticlesUrl, wagoArticleFavoriteDTO, null, { isErrorHandlingRequired: false });
  }

  public loadAllArticleTypeClasses(): Observable<ArticleTypeClassVO[]> {
    return this.wagoApiClient
      .get(this.loadAllArticleTypeClassesURL)
      .pipe(map((response: any[]) => this.modelConversionService.convertArticleTypeClassVOs(response)));
  }

  public loadAllMCSArticleTypeClasses(): Observable<ArticleTypeClassVO[]> {
    return this.wagoApiClient
      .get(this.loadAllMCSArticleTypeClassesURL)
      .pipe(map((response: any[]) => this.modelConversionService.convertArticleTypeClassVOs(response)));
  }

  public loadMainNotesForTypeClassId(typeClassId: string): Observable<Object[]> {
    return this.wagoApiClient
      .get(`article/main-note/search/type-class/${typeClassId}`)
      .pipe(map((response) => this.modelConversionService.convertObjects(response)));
  }

  public loadAdditionalNotesForMainNote(id: string): Observable<Object[]> {
    return this.wagoApiClient
      .get(`article/additional-note/search/attribute/${id}`)
      .pipe(map((response) => this.modelConversionService.convertObjects(response)));
  }

  public loadMCSMainNotesForTypeClassId(id: string): Observable<Object[]> {
    return this.wagoApiClient
      .get(`article/main-note/mcs/search/type-class/${id}`)
      .pipe(map((response) => this.modelConversionService.convertObjects(response)));
  }

  public loadMCSProductFamiliesForTypeClassId(id: string): Observable<Object[]> {
    return this.wagoApiClient
      .get(`article/product-family/mcs/search/type-class/${id}`)
      .pipe(map((response) => this.modelConversionService.convertObjects(response)));
  }

  public loadMCSAdditionalNotesForTypeClassId(id: string): Observable<Object[]> {
    return this.wagoApiClient
      .get(`article/additional-note/mcs/search/type-class/${id}`)
      .pipe(map((response) => this.modelConversionService.convertObjects(response)));
  }

  public calculateProjectPrice(
    requestVO: PriceCalculationRequestVO,
    projectVO: PriceCalculationRequestProjectVO
  ): Observable<PriceCalculationResultVO> {
    const dto: any = {
      requestVO,
      projectVO,
    };
    return this.wagoApiClient
      .post(this.calculatePriceUrl, dto, null, { isErrorHandlingRequired: false })
      .pipe(map((response) => this.modelConversionService.convertPriceCalculationResultVO(response)));
  }

  public loadAllDrillingHoles(): Observable<DrillingHoleDataVO> {
    return this.wagoApiClient.get(this.loadAllDrillingHolesUrl).pipe(
      map((response) => this.modelConversionService.convertDrillingHoleDataVO(response))
    );
  }

  public getItemListByProject(project: ProjectVO): Observable<ItemListObjectVO[]> {
    return this.wagoApiClient
      .post(this.getItemListByProjectUrl, project, null, { isErrorHandlingRequired: false })
      .pipe(map((response) => this.modelConversionService.convertItemListObjectVOs(response)));
  }

  public getItemListByProjectId(projectId: number, projectVersion: number): Observable<ItemListObjectVO[]> {
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('id', projectId.toString());
    httpParams = httpParams.append('version', projectVersion.toString());
    const requestOptions = {
      params: httpParams,
    };

    return this.wagoApiClient
      .get(this.getItemListByProjectIdUrl, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertItemListObjectVOs(response)));
  }

  public getAllArticleTypes(): Observable<ArticleTypeVO[]> {
    return this.wagoApiClient
      .get(`article/types`)
      .pipe(map((data) => this.modelConversionService.convertArticleTypeVOs(data)));
  }

  public getArticleColorVariants(articleNumbers: String[]): Observable<ArticleColorVariantsInfoVO[]> {
    if (!articleNumbers || articleNumbers.length <= 0) {
      return of(new Array<ArticleColorVariantsInfoVO>());
    }
    const requestOptions = {
      params: new HttpParams().append('language', this.localeResourceManager.currentLanguage.langKey),
    };
    return this.wagoApiClient
      .post<ArticleColorVariantsInfoVO[]>(this.getArticleColorVariantsUrl, articleNumbers, requestOptions)
      .pipe(
        map((data) => {
          if(data.length == 0) {
            Log.error(`Received empty array of colorVariants for articles '${articleNumbers}'`);
          }
          return this.modelConversionService.convertArticleColorVariantsInfoVO(data);
        })
      );
  }

  public mapArticleNosToArticleHeads(articleNos: string[]): Observable<ArticleNosToHeadMappingVO> {
    if (articleNos.length === 0) {
      return of(null);
    }
    return this.wagoApiClient
      .get(`${this.getArticleHeadsByArticleNosUrl}?articleNos=${articleNos}`)
      .pipe(map((response) => this.modelConversionService.convertArticleNosToHeadMappingVO(response)));
  }

  public loadArticleAccessoryInfos(articleIds: number[]): Observable<ArticleAccessoryInfoVO[]> {
    if (ArrayUtils.isEmpty(articleIds)) {
      return of([]);
    }

    const requestOptions = {
      params: new HttpParams().append('articleIds', articleIds.join()),
    };

    const parameters: Parameters = { overlayType: null };

    return this.wagoApiClient
      .get(`article/info/accessories`, requestOptions, parameters)
      .pipe(map((data: any) => {
        return this.modelConversionService.convertArticleAccessoryInfoVOs(data);
      }));
  }

  public loadArticleAccessoryInfo(articleId: number): Observable<ArticleAccessoryInfoVO> {
    return this.wagoApiClient
      .get(`article/${articleId}/info/accessory`)
      .pipe(map((data: any) => {
        return this.modelConversionService.convertArticleAccessoryInfoVO(data);
      }));
  }

  public findTypeIdsByArticleIds(articleIds: number[]): Observable<number[]> {
    return this.wagoApiClient
      .get(`${this.findTypeIdsByArticleIdsUrl}?articleIds=${articleIds.toString()}`);
  }

  public getArticleNumberMappingsByOldArticleNumbers(oldArticleNumbers: string[]): Observable<OldArticleNumberMappingVO[]> {
    const requestOptions = {
      params: new HttpParams().append('oldArticleNumbers', oldArticleNumbers.join()),
    };

    return this.wagoApiClient.get(this.loadArticleByOldArticleNumbersUrl, requestOptions)
      .pipe(map((response: any) => this.modelConversionService.convertOldArticleNumberMappingVOs(response)));
  }

  public searchArticlesByRequirements(
    locale: string,
    searchText: string,
    articleTypeIds: number[],
    series: string[],
    technicalRequirements: TechnicalRequirementsVO[],
    technicalProperties: string[],
  ): Observable<ArticleWithTechnicalPropertiesDTO[]> {

    const articleSearch: SearchArticlesByTechnicalPropertiesRequestDTO = {
      locale,
      searchText,
      articleTypeIds,
      series,
      technicalRequirements,
      technicalProperties
    }

    const parameters: Parameters = { overlayType: null };

    return this.wagoApiClient.post<ArticleWithTechnicalPropertiesDTO[]>('article/search/by-requirements', articleSearch, null, parameters);
  }

  public getArticleInfos(articleIds: number[], propertyNames: string[]): Observable<ArticleWithTechnicalPropertiesDTO[]> {
    const locale = this.localeResourceManager.currentLanguage.langKey;
    const data = new ArticlesWithTechnicalPropertiesRequestDTO(articleIds, propertyNames);
    return this.wagoApiClient.post<ArticleWithTechnicalPropertiesDTO[]>('article/tech-props?locale=' + locale, data);
  }

  public getVersionsByArticleId(articleId: number): Observable<ArticleVO[]> {
    let params: HttpParams = new HttpParams();
    params = params.append('articleId', articleId);
    const requestOptions = {
      params: params,
    };
    return this.wagoApiClient.get<ArticleVO[]>('article/versions', requestOptions);
  }
}
